import { createDeptInputSchema, updateDeptInputSchema } from '@kiku/admin/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { InferResponseType } from 'hono/client';
import { MoreVertical } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CrudDialog } from './crud-dialog';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '~/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '~/components/ui/table';
import { api } from '~/lib/api';
import { extractApiError } from '~/utils/api';
import { formatDate } from '~/utils/date';

type DeptsResponse = InferResponseType<
  (typeof api.api.admin.organizations)[':orgId']['departments']['$get']
>;
type Dept = DeptsResponse['departments'][number];

type DeptTableProps = {
  orgId: string;
  departments: Dept[];
  createOpen: boolean;
  onCreateOpenChange: (open: boolean) => void;
};

export function DeptTable({
  orgId,
  departments,
  createOpen,
  onCreateOpenChange
}: DeptTableProps): React.ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [editDept, setEditDept] = useState<Dept | null>(null);

  const createMutation = useMutation({
    mutationFn: async (values: { name: string; slug: string }) => {
      const res = await api.api.admin.organizations[':orgId'].departments.$post({
        param: { orgId },
        json: { name: values.name, slug: values.slug }
      });
      return res.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'departments', orgId] });
      onCreateOpenChange(false);
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, name, slug }: { id: string; name?: string; slug?: string }) => {
      const res = await api.api.admin.departments[':id'].$patch({
        param: { id },
        json: { name, slug }
      });
      if (!res.ok) {
        throw new Error(extractApiError(await res.json(), 'Failed to update department'));
      }
      return res.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'departments', orgId] });
      setEditDept(null);
    }
  });

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
              {t('admin.departments.columns.name')}
            </TableHead>
            <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
              {t('admin.departments.columns.slug')}
            </TableHead>
            <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
              {t('admin.departments.columns.createdAt')}
            </TableHead>
            <TableHead className="w-12 text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
              {t('admin.departments.columns.actions')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {departments.map((dept) => (
            <TableRow key={dept.id} className="group">
              <TableCell className="py-3 text-sm text-foreground">{dept.name}</TableCell>
              <TableCell className="py-3 text-sm text-muted-foreground">{dept.slug}</TableCell>
              <TableCell className="py-3 text-sm text-muted-foreground">
                {formatDate(dept.createdAt)}
              </TableCell>
              <TableCell className="py-3 text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent">
                    <MoreVertical className="size-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => {
                        setEditDept(dept);
                      }}
                    >
                      {t('actions.edit')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <CrudDialog
        open={createOpen}
        onOpenChange={(open) => {
          onCreateOpenChange(open);
          if (!open) createMutation.reset();
        }}
        title={t('admin.departments.createDialog.title')}
        fields={[
          {
            name: 'name',
            label: t('admin.departments.columns.name'),
            placeholder: t('admin.departments.createDialog.namePlaceholder')
          },
          {
            name: 'slug',
            label: t('admin.departments.columns.slug'),
            placeholder: t('admin.departments.createDialog.slugPlaceholder')
          }
        ]}
        schema={createDeptInputSchema.omit({ orgId: true })}
        onSubmit={(values) => {
          createMutation.mutate({ name: values['name'] ?? '', slug: values['slug'] ?? '' });
        }}
        isPending={createMutation.isPending}
        error={createMutation.error?.message}
        submitLabel={t('admin.departments.newDept')}
      />

      <CrudDialog
        open={editDept !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditDept(null);
            updateMutation.reset();
          }
        }}
        title={t('admin.departments.editDialog.title')}
        fields={[
          {
            name: 'name',
            label: t('admin.departments.columns.name'),
            placeholder: t('admin.departments.createDialog.namePlaceholder')
          },
          {
            name: 'slug',
            label: t('admin.departments.columns.slug'),
            placeholder: t('admin.departments.createDialog.slugPlaceholder')
          }
        ]}
        defaultValues={{ name: editDept?.name ?? '', slug: editDept?.slug ?? '' }}
        schema={updateDeptInputSchema}
        onSubmit={(values) => {
          if (!editDept) return;
          updateMutation.mutate({
            id: editDept.id,
            name: values['name'],
            slug: values['slug']
          });
        }}
        isPending={updateMutation.isPending}
        error={updateMutation.error?.message}
        submitLabel={t('actions.save')}
      />
    </>
  );
}
